import React, { useEffect, useState } from 'react';
import { DatePicker, Spin } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import toast from 'react-hot-toast';
import {
  getRevenueReport,
  RevenueReport,
} from '../../../../api/payment/withdraw-request.api';
import { Coins, Download, Info, Landmark, TrendingDown, TrendingUp } from 'lucide-react';
import { AdminButton, AdminPageHeader, AdminSectionTitle, AdminStatGrid } from '../../ui';
import TransactionLedger from './TransactionLedger';
import BrandLedgerSection from './BrandLedgerSection';
import CreatorIncomeSection from './CreatorIncomeSection';

const { RangePicker } = DatePicker;

const fmtVnd = (n?: number) =>
  `${Number(n || 0).toLocaleString('vi-VN')}đ`;

const TONE_COLOR = {
  brand: 'var(--adm-on-soft)',
  success: 'var(--adm-success)',
  warning: 'var(--adm-warning)',
  danger: 'var(--adm-danger)',
};

const Card: React.FC<{
  icon: React.ReactNode;
  tone: 'brand' | 'success' | 'warning' | 'danger';
  title: string;
  value: string;
  sub: React.ReactNode;
}> = ({ icon, tone, title, value, sub }) => (
  <div className="adm-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
    <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
      <span className={`adm-stat__icon adm-stat__icon--${tone}`} aria-hidden="true">
        {icon}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--adm-muted)' }}>{title}</div>
        <div style={{ marginTop: 4, fontSize: 24, lineHeight: '32px', fontWeight: 700, color: TONE_COLOR[tone] }}>
          {value}
        </div>
      </div>
    </div>
    <div
      style={{
        paddingTop: 12,
        borderTop: '1px solid var(--adm-line)',
        fontSize: 12,
        lineHeight: '18px',
        color: 'var(--adm-muted)',
      }}
    >
      {sub}
    </div>
  </div>
);

const ServiceCard: React.FC<{ title: string; value: string; span: number }> = ({ title, value, span }) => (
  <div className="adm-panel" style={{ gridColumn: `span ${span}`, padding: 16 }}>
    <div style={{ fontSize: 13, lineHeight: '18px', color: 'var(--adm-muted)' }}>{title}</div>
    <div style={{ fontSize: 20, lineHeight: '28px', fontWeight: 700, color: 'var(--adm-heading)', marginTop: 4 }}>
      {value}
    </div>
  </div>
);

const InfoCard: React.FC<{ title: string; value: string; sub: string }> = ({ title, value, sub }) => (
  <div className="adm-card">
    <div style={{ fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--adm-muted)' }}>{title}</div>
    <div style={{ fontSize: 24, lineHeight: '32px', fontWeight: 700, color: 'var(--adm-heading)', margin: '4px 0 8px' }}>
      {value}
    </div>
    <div style={{ fontSize: 12, lineHeight: '18px', color: 'var(--adm-muted)' }}>{sub}</div>
  </div>
);

const ManagementRevenueReport: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState<RevenueReport | null>(null);
  // Mặc định: tháng hiện tại
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>([
    dayjs().startOf('month'),
    dayjs().endOf('day'),
  ]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const from = range?.[0]?.startOf('day').toISOString();
      const to = range?.[1]?.endOf('day').toISOString();
      const data = await getRevenueReport(from, to);
      setReport(data);
    } catch (e) {
      toast.error('Không tải được báo cáo doanh thu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range]);

  const exportCsv = () => {
    if (!report) return;
    const rows: [string, number][] = [
      ['Phí nạp (gross-up)', report.topUpGrossUpRevenue],
      ['Phí rút', report.withdrawPlatformFee],
      ['Quảng cáo cá nhân', report.adRevenuePersonal],
      ['Quảng cáo doanh nghiệp', report.adRevenueBusiness],
      ['Cab+ Pro', report.cabPlusRevenue],
      ['Hội viên kênh', report.membershipRevenue],
      ['Trợ lý (token)', report.aiRevenue],
      ['Tiếp thị liên kết', report.affiliateRevenue],
      ['Hợp tác creator', report.brandDealFeeRevenue],
      ['TỔNG DOANH THU', report.totalRevenue],
      ['Chi phí VNPay (ước tính)', -report.totalVnpayCostEst],
      ['Chi phí nhà cung cấp Trợ lý (ước tính)', -report.aiProviderCostEst],
      ['Chia creator từ quảng cáo', -report.creatorAdShareCost],
      ['Chi trả lượt Cab+', -report.membershipSlotPayout],
      ['LỢI NHUẬN GỘP (ước tính)', report.grossProfitEst],
      ['Thuế TNCN thu hộ (không phải DT)', report.taxWithheldTotal],
      ['Tiền nạp gốc (nợ phải trả)', report.topUpBaseVnd],
      ['Đã chi trả user (rút)', report.withdrawNetTransfer],
    ];
    const csv = [
      `Báo cáo doanh thu,${range?.[0]?.format('DD/MM/YYYY')} - ${range?.[1]?.format('DD/MM/YYYY')}`,
      'Khoản mục,Số tiền (VND)',
      ...rows.map(([k, v]) => `"${k}",${v}`),
    ].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `bao-cao-doanh-thu-${range?.[0]?.format('YYYYMMDD')}-${range?.[1]?.format('YYYYMMDD')}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="adm-page">
      <AdminPageHeader
        title="Báo cáo doanh thu – chi phí – lợi nhuận"
        description="Tổng hợp doanh thu, chi phí, lợi nhuận và các sổ giao dịch theo khoảng ngày."
        actions={
          <>
            <RangePicker
              value={range as any}
              format="DD/MM/YYYY"
              allowClear={false}
              onChange={(v) => v && setRange(v as [Dayjs, Dayjs])}
            />
            <AdminButton elevated onClick={exportCsv} disabled={!report} icon={<Download size={18} />}>
              Xuất CSV
            </AdminButton>
          </>
        }
      />

      <div className="adm-strip" role="status">
        <Info size={20} className="adm-strip__icon" />
        <p style={{ margin: 0 }}>
          Tiền user nạp là <b>nợ phải trả</b> (không tính doanh thu). Doanh thu = <b>phí nạp/rút</b> + <b>doanh thu dịch vụ</b>
          {' '}(Quảng cáo cá nhân/DN, Cab+ Pro, hội viên kênh, token Trợ lý — ghi nhận khi dịch vụ được cung cấp). Chi phí VNPay là <b>ước tính</b> theo phí{' '}
          {report ? `${report.vnpayActualFeePercent}% + ${fmtVnd(report.vnpayActualFixedFeeVnd)}/GD` : '1,1% + 1.650đ/GD'}
          {' '}— đối soát sao kê VNPay cho số chính xác. Chi phí nhà cung cấp Trợ lý là <b>ước tính</b> theo lượng đã dùng (đối soát hoá đơn nhà cung cấp).
        </p>
      </div>

      {loading ? (
        <div className="min-h-[160px] flex justify-center items-center">
          <Spin />
        </div>
      ) : report ? (
        <>
          {/* Hàng tổng hợp chính */}
          <AdminStatGrid>
            <Card
              icon={<TrendingUp size={20} />}
              tone="brand"
              title="Tổng doanh thu"
              value={fmtVnd(report.totalRevenue)}
              sub={
                <>
                  Phí nạp: {fmtVnd(report.topUpGrossUpRevenue)} · Phí rút: {fmtVnd(report.withdrawPlatformFee)}
                  <br />
                  Dịch vụ (Ads/Cab+/hội viên kênh/AI): {fmtVnd(report.serviceRevenue)}
                </>
              }
            />
            <Card
              icon={<TrendingDown size={20} />}
              tone="danger"
              title="Chi phí (ước tính)"
              value={`− ${fmtVnd(report.totalVnpayCostEst + report.aiProviderCostEst + report.publisherShareCost + report.creatorAdShareCost + report.membershipSlotPayout)}`}
              sub={
                <>
                  VNPay: {fmtVnd(report.totalVnpayCostEst)}
                  <br />
                  Nhà cung cấp Trợ lý: {fmtVnd(report.aiProviderCostEst)}
                  <br />
                  Chia publisher (Ads Network): {fmtVnd(report.publisherShareCost)}
                  <br />
                  Chia creator từ quảng cáo: {fmtVnd(report.creatorAdShareCost)}
                  <br />
                  Chi trả lượt Cab+: {fmtVnd(report.membershipSlotPayout)}
                </>
              }
            />
            <Card
              icon={<Coins size={20} />}
              tone="success"
              title="Lợi nhuận gộp (ước tính)"
              value={fmtVnd(report.grossProfitEst)}
              sub="= Doanh thu − Chi phí (VNPay + Trợ lý + chia Publisher + chia creator từ quảng cáo + chi trả lượt Cab+)"
            />
            <Card
              icon={<Landmark size={20} />}
              tone="warning"
              title="Thuế TNCN thu hộ"
              value={fmtVnd(report.taxWithheldTotal)}
              sub="Thu hộ nhà nước — KHÔNG phải doanh thu"
            />
          </AdminStatGrid>

          {/* Doanh thu dịch vụ — tách theo nguồn */}
          <section className="adm-stack">
            <AdminSectionTitle title={`Doanh thu dịch vụ: ${fmtVnd(report.serviceRevenue)}`} />
            <div
              className="adm-card"
              style={{ display: 'grid', gridTemplateColumns: 'repeat(12, minmax(0, 1fr))', gap: 12 }}
            >
              <ServiceCard span={3} title="Quảng cáo cá nhân" value={fmtVnd(report.adRevenuePersonal)} />
              <ServiceCard span={3} title="Quảng cáo doanh nghiệp" value={fmtVnd(report.adRevenueBusiness)} />
              <ServiceCard span={3} title="Cab+ Pro" value={fmtVnd(report.cabPlusRevenue)} />
              <ServiceCard span={3} title="Hội viên kênh" value={fmtVnd(report.membershipRevenue)} />
              <ServiceCard span={4} title="Trợ lý (token)" value={fmtVnd(report.aiRevenue)} />
              <ServiceCard span={4} title="Tiếp thị liên kết" value={fmtVnd(report.affiliateRevenue)} />
              <ServiceCard span={4} title="Hợp tác creator" value={fmtVnd(report.brandDealFeeRevenue)} />
            </div>

            {/* Hàng bổ trợ */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
              <InfoCard
                title="Tiền nạp gốc (nợ phải trả)"
                value={fmtVnd(report.topUpBaseVnd)}
                sub={`${report.topUpCount} giao dịch nạp thành công · Ca trả trước, user tiêu/rút được`}
              />
              <InfoCard
                title="Đã chi trả cho user (rút)"
                value={fmtVnd(report.withdrawNetTransfer)}
                sub={`${report.withdrawCount} lệnh rút đã hoàn thành (D thực chuyển)`}
              />
            </div>
          </section>
        </>
      ) : (
        <div className="min-h-[120px] flex justify-center items-center text-[#62677D]">
          Chưa có dữ liệu
        </div>
      )}

      {/* Sổ giao dịch chi tiết — drill-down từng khoản (gồm cả mua vật phẩm) */}
      <TransactionLedger
        fromDate={range?.[0]?.toISOString()}
        toDate={range?.[1]?.toISOString()}
      />

      {/* Sàn hợp tác creator: sổ dòng tiền cho cơ quan thuế và tổng thu nhập từng creator. Khoảng ngày riêng, theo giờ Việt Nam. */}
      <BrandLedgerSection />
      <CreatorIncomeSection />
    </div>
  );
};

export default ManagementRevenueReport;
